import {
  ActivityIcon,
  BotIcon,
  CpuIcon,
  GaugeIcon,
  GraduationCapIcon,
  SearchCheckIcon,
  SlidersHorizontalIcon,
  WrenchIcon,
  ZapIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentals of Industrial Instrumentation: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to industrial instrumentation can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Introduction to Industrial Instrumentation',
      'Purpose and Role of Instrumentation',
      'Industrial Processes & Process Variables',
      'Measurement Fundamentals',
      'Basic Instrument Terminology',
      'Sensors, Transducers & Transmitters',
      'Industrial Measurement Chains',
      'Indicators, Recorders & Controllers',
      'Introduction to Industrial Signals',
      'Basic Control Concepts',
      'Instrument Identification & Applications',
      'Instrumentation in Real Industrial Environments',
      'Fundamental Technical Reasoning',
      'Classroom Examples & Applications',
      'Review & Teacher Guidance',
    ],
    result:
      'Your students understand industrial instrumentation as a practical discipline that connects measurement science, process control, and operational excellence — not an impossible list of terms to memorize.',
    icon: WrenchIcon,
  },
  {
    number: '2',
    title: 'Industrial Measurement Principles & Instrument Performance',
    subtitle: 'Everything your students need to know about measurement accuracy and instrument evaluation',
    context:
      'This is where many students struggle to build the measurement foundation. This volume gives you the materials to help them truly understand industrial measurement.',
    items: [
      'Measurement Principles',
      'Range & Span',
      'Accuracy & Precision',
      'Resolution & Sensitivity',
      'Repeatability & Reproducibility',
      'Linearity',
      'Hysteresis',
      'Measurement Error',
      'Measurement Uncertainty',
      'Instrument Drift',
      'Static & Dynamic Performance',
      'Instrument Selection',
      'Measurement Quality',
      'Industrial Measurement Examples',
      'Calculations & Technical Reasoning',
      'Teacher Guidance & Review',
      '15 worked measurement performance examples',
    ],
    result: 'Your students learn the measurement principles that underpin all industrial instrumentation work.',
    icon: GaugeIcon,
  },
  {
    number: '3',
    title: 'Sensors, Transducers & Industrial Signals',
    subtitle: 'How physical process variables are detected, converted, and transmitted in industrial systems',
    context:
      'Industrial signals are critical but complex. This volume gives you everything needed so your students grasp sensor technology clearly.',
    items: [
      'Sensors & Sensing Elements',
      'Transducers',
      'Industrial Transmitters',
      'Sensor vs. Transducer vs. Transmitter',
      'Measurement Chains',
      'Analog Signals',
      '4–20 mA Signals',
      'Voltage Signals',
      'Digital Signals',
      'Signal Conversion',
      'Signal Scaling',
      'Signal Transmission',
      'Signal Integrity',
      'Basic Wiring Concepts',
      'Common Signal Problems',
      'Sensor & Transmitter Selection',
      'Practical Industrial Applications',
      '15 worked sensor and signal examples',
    ],
    result:
      'Your students understand how physical measurements are converted into industrial signals and how to interpret them.',
    icon: ZapIcon,
  },
  {
    number: '4',
    title: 'Temperature, Pressure, Level & Flow Measurement',
    subtitle: 'Explore the four major process variables and the instruments used to measure them',
    context:
      'Process variables are the heart of industrial control. This volume makes these critical measurements clear and applicable.',
    items: [
      'Temperature Measurement',
      'Thermocouples',
      'RTDs',
      'Temperature Transmitters',
      'Pressure Measurement',
      'Pressure Instruments & Transmitters',
      'Differential Pressure',
      'Level Measurement Principles',
      'Level Instruments & Applications',
      'Flow Measurement Principles',
      'Flowmeters & Industrial Applications',
      'Instrument Selection',
      'Measurement Errors',
      'Comparing Measurement Technologies',
      'Industrial Scenarios',
      'Troubleshooting Reasoning',
      'Classroom Applications',
      '15 practical process variable case studies',
    ],
    result:
      'Your students can select appropriate instruments for temperature, pressure, level, and flow measurement and understand their limitations.',
    icon: ActivityIcon,
  },
  {
    number: '5',
    title: 'Industrial Control Systems & Control Loops',
    subtitle: 'Move from measurement into the fundamentals of automatic process control',
    context:
      'Control loops are the foundation of industrial automation. This volume makes these systems understandable.',
    items: [
      'Fundamentals of Process Control',
      'Open-Loop & Closed-Loop Control',
      'Process Variable — PV',
      'Setpoint — SP',
      'Manipulated Variable — MV',
      'Controllers',
      'Feedback Control',
      'Control Loop Components',
      'Process Response',
      'On/Off Control',
      'Proportional Control',
      'Integral Action',
      'Derivative Action',
      'PID Control Fundamentals',
      'Control Loop Behavior',
      'Common Control Problems',
      'Industrial Control Scenarios',
      'Technical Reasoning Activities',
      '12 worked control loop examples',
    ],
    result:
      'Your students understand how control loops work and how instrumentation enables automatic process control.',
    icon: CpuIcon,
  },
  {
    number: '6',
    title: 'Control Valves, Actuators & Final Control Elements',
    subtitle: 'How controller decisions are transformed into physical changes in processes',
    context:
      'Control valves and actuators complete the control loop. This volume makes their operation and selection clear.',
    items: [
      'Final Control Elements',
      'Control Valve Fundamentals',
      'Main Control Valve Components',
      'Valve Types',
      'Valve Characteristics',
      'Actuators',
      'Pneumatic Actuation',
      'Electric Actuation',
      'Valve Positioners',
      'Control Signals',
      'Fail Positions',
      'Valve Selection',
      'Valve Response',
      'Common Valve Problems',
      'Actuator Problems',
      'Control Loop Interaction',
      'Industrial Applications',
      'Troubleshooting & Technical Reasoning',
      '12 worked control valve and actuator examples',
    ],
    result:
      'Your students understand how final control elements operate and how they integrate with measurement and control systems.',
    icon: SlidersHorizontalIcon,
  },
  {
    number: '7',
    title: 'PLC, SCADA & Industrial Automation Fundamentals',
    subtitle: 'Introduce students to the systems used to automate and monitor modern industrial processes',
    context:
      'Industrial automation systems are the modern standard. This volume makes them accessible and understandable.',
    items: [
      'Industrial Automation Fundamentals',
      'PLC Fundamentals',
      'PLC Hardware',
      'Inputs & Outputs',
      'Digital & Analog I/O',
      'Basic PLC Logic',
      'Automation Sequences',
      'Interlocks',
      'Permissives',
      'HMI Fundamentals',
      'SCADA Fundamentals',
      'Process Monitoring',
      'Alarms',
      'Industrial Communication Concepts',
      'Instrument-to-PLC Signal Flow',
      'PLC, HMI & SCADA Integration',
      'Automation Troubleshooting',
      'Industrial Applications',
      '15 practical PLC and automation case studies',
    ],
    result:
      'Your students understand how PLCs, HMIs, and SCADA systems integrate with instrumentation to automate industrial processes.',
    icon: BotIcon,
  },
  {
    number: '8',
    title: 'Calibration, Maintenance, Troubleshooting & Instrument Safety',
    subtitle: 'Develop the reasoning students need to evaluate instrument performance and identify problems',
    context:
      'Instrumentation maintenance and troubleshooting are essential professional skills. This volume develops these competencies.',
    items: [
      'Calibration Fundamentals',
      'Calibration vs. Adjustment vs. Verification',
      'Calibration References',
      'As-Found & As-Left Concepts',
      'Instrument Accuracy Verification',
      'Maintenance Fundamentals',
      'Preventive Maintenance',
      'Instrument Reliability',
      'Instrument Drift',
      'Troubleshooting Methodology',
      'Evidence-Based Diagnosis',
      'Measurement Chain Troubleshooting',
      'Common Instrument Failures',
      'Instrument Safety Fundamentals',
      'Safety Instrumented Systems Concepts',
      'Responsible Technical Decisions',
      'Industrial Cases',
      'Classroom Activities & Teacher Guidance',
      '12 worked calibration and troubleshooting examples',
    ],
    result:
      'Your students can verify instrument performance, identify measurement problems, and understand instrumentation safety requirements.',
    icon: SearchCheckIcon,
  },
  {
    number: '9',
    title: 'Teaching Industrial Instrumentation: Methods, Activities & Classroom Practice',
    subtitle: 'Your complete guide as an industrial instrumentation education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of industrial instrumentation teaching.',
    items: [
      'How to Teach Industrial Instrumentation',
      'Building Learning Progressions',
      'Course Planning',
      'Learning Objectives',
      'Explaining Technical Concepts',
      'Using Analogies Effectively',
      'Teaching Measurement',
      'Teaching Sensors & Signals',
      'Teaching Process Variables',
      'Teaching Control Loops',
      'Teaching PLC & Automation',
      'Teaching Calibration & Maintenance',
      'Teaching Troubleshooting',
      'Questioning Strategies',
      'Classroom Activities',
      'Case-Based Learning',
      'Assessment Strategies',
      'Common Student Misconceptions',
      'Differentiated Instruction',
      'Teaching Safety',
      'Sample Teaching Sequences',
      'Teacher Resource Section',
      '10 complete integrated instrumentation projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
